import { useCallback, useState } from 'react';
import { Card } from '../components/Painel';
import {
  api,
  ApiError,
  type AudienceImportPreviewResponse,
  type AudienceImportsResponse,
  type CorpoDaRecusaDaImportacao,
  type MapeamentoDeColunasDaImportacao,
  type RecusaDaImportacao,
} from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';
import { Confirmacao } from '../components/Confirmacao';

/**
 * Importar audiência por arquivo (Console SPEC v1.5 §186 e §187).
 *
 * Quatro regras que esta tela existe para não quebrar:
 *
 * 1. **o número que a pessoa confirma é o que ENTRA.** `rows_that_will_be_imported`
 *    e `counts.total` são diferentes, e o total inclui linha repetida, linha sem
 *    identificador e linha inválida. Este projeto já mediu o custo de contar uma
 *    coisa e enviar outra na própria audiência: quatro selecionados, um enviado.
 *    Por isso os dois aparecem lado a lado, com o que cada um significa, e o
 *    botão ecoa o que entra;
 *
 * 2. **a prévia não mostra e-mail nem telefone de ninguém.** A resposta traz o
 *    que é descritivo (cidade, cargo) e quais identificadores a linha TEM, sem
 *    dizer quais são. Conferir mapeamento se faz pelo FORMATO do que aparece;
 *    mostrar o identificador transformaria a conferência numa exportação de dado
 *    pessoal para a tela, para o console do navegador e para qualquer captura;
 *
 * 3. **a declaração de base legal é obrigatória, e a tela diz a consequência.**
 *    Sem ela a lista existe e não envia ninguém, porque as marcas de exclusão
 *    ficam desconhecidas e desconhecido EXCLUI. Uma lista assim pareceria pronta
 *    e ninguém entenderia por que o envio sai zerado;
 *
 * 4. **o catálogo de campos e as colunas vêm da RESPOSTA**, inclusive da recusa.
 *    Nenhuma lista de campo do §187 é escrita aqui, e nenhum CSV é analisado
 *    aqui: um segundo analisador detectaria separador e aspas diferente do da
 *    API e ofereceria colunas que o servidor não enxerga, e a pessoa mapearia
 *    para o nada.
 *
 * O arquivo é lido no NAVEGADOR e o conteúdo vai no corpo. Não existe upload
 * multipart nesta API, de propósito: multipart exigiria uma dependência nova no
 * grafo do app, e CSV é texto.
 */

/**
 * Tradução dos nomes de campo do catálogo.
 *
 * Isto NÃO é o catálogo: quem diz quais campos existem é `mappable_fields` da
 * resposta, e um nome que não estiver aqui aparece como veio. Uma lista escrita
 * aqui viraria a segunda cópia do §187, e a divergência seria silenciosa.
 */
const ROTULO_DO_CAMPO: Record<string, string> = {
  email: 'e-mail',
  phone: 'telefone',
  first_name: 'nome',
  last_name: 'sobrenome',
  company: 'empresa',
  role: 'cargo',
  city: 'cidade',
  state: 'estado',
  zip: 'CEP',
  country: 'país',
  internal_id: 'identificador interno',
};

function rotuloDoCampo(campo: string): string {
  return ROTULO_DO_CAMPO[campo] ?? campo;
}

const ROTULO_DO_VEREDITO: Record<string, string> = {
  valida: 'válida',
  aviso: 'com aviso',
  invalida: 'inválida',
};

const ROTULO_DO_ESTADO_DA_IMPORTACAO: Record<string, string> = {
  UPLOADED: 'arquivo recebido',
  MAPPED: 'mapeada',
  VALIDATED: 'validada',
  IMPORTED: 'importada',
  FAILED: 'falhou',
};

type ArquivoEscolhido = {
  readonly nome: string;
  readonly tipo: 'CSV' | 'XLSX';
  readonly texto: string;
};

/**
 * As colunas do arquivo e os campos que o servidor aceita.
 *
 * Vem da prévia quando ela passa, e da RECUSA quando ela recusa por mapeamento.
 * É o mesmo dado pelas duas portas, e por isso mora em um estado só: a tela
 * monta o seletor do mesmo jeito nos dois casos.
 */
type Catalogo = {
  readonly headers: readonly string[];
  readonly mappable_fields: readonly string[];
  readonly matching_fields: readonly string[];
};

/**
 * O que a última conferência devolveu, além da prévia.
 *
 * `aguardando_mapeamento` NÃO é erro da pessoa: é o passo "li o seu arquivo,
 * estas são as colunas dele, aponte ao menos um identificador". Ele nasce da
 * conferência automática que a tela dispara com o mapeamento vazio, e pintá-lo
 * de vermelho faria quem acabou de escolher um arquivo achar que errou alguma
 * coisa.
 *
 * `recusa` é recusa de verdade: veio depois de a pessoa já ter apontado colunas,
 * ou o arquivo nem foi lido.
 */
type Passo =
  | { readonly tipo: 'aguardando_mapeamento'; readonly recusas: readonly RecusaDaImportacao[] }
  | {
      readonly tipo: 'recusa';
      readonly codigo: string;
      readonly mensagem: string;
      readonly recusas: readonly RecusaDaImportacao[];
    }
  | null;

type ResultadoDoCommit = {
  readonly importId: string;
  readonly linhasGravadas: number;
};

export function AudienceImport() {
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  return (
    <div className="max-w-5xl space-y-4 text-sm">
      <h1 className="text-2xl font-bold">Importar audiência por arquivo</h1>
      <p className="mt-2 text-ink-muted">
        O arquivo é lido aqui no navegador e o conteúdo vai no corpo da requisição. Nada é gravado
        enquanto você só confere: a prévia lê, mapeia, valida e deduplica sem criar lista nenhuma.
      </p>

      <Importador aoImportar={recarregar} />
      <ImportacoesJaFeitas versao={versao} />
    </div>
  );
}

// ===========================================================================
// O importador
// ===========================================================================

function Importador({ aoImportar }: { aoImportar: () => void }) {
  const [arquivo, setArquivo] = useState<ArquivoEscolhido | null>(null);
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [mapeamento, setMapeamento] = useState<MapeamentoDeColunasDaImportacao>({});
  const [previa, setPrevia] = useState<AudienceImportPreviewResponse | null>(null);
  /**
   * O mapeamento que PRODUZIU a prévia na tela.
   *
   * Sem isto, mudar um campo depois de conferir deixaria na tela um número
   * calculado com outro mapeamento, e é esse número que a pessoa confirmaria. O
   * servidor recusaria a divergência sem gravar, mas quem opera veria uma recusa
   * sem entender de onde ela veio.
   */
  const [mapeamentoDaPrevia, setMapeamentoDaPrevia] = useState<string | null>(null);
  const [passo, setPasso] = useState<Passo>(null);

  const [baseLegal, setBaseLegal] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDoCommit | null>(null);

  function limpar() {
    setCatalogo(null);
    setMapeamento({});
    setPrevia(null);
    setMapeamentoDaPrevia(null);
    setPasso(null);
    setResultado(null);
  }

  async function escolherArquivo(file: File | null) {
    limpar();
    if (file === null) {
      setArquivo(null);
      return;
    }
    // O tipo sai da EXTENSÃO e o servidor é quem recusa XLSX, com o motivo.
    // Esconder o botão aqui inventaria uma validação própria, e a pessoa ficaria
    // sem saber por que o arquivo dela não serve.
    const tipo = file.name.toLowerCase().endsWith('.xlsx') ? 'XLSX' : 'CSV';
    let escolhido: ArquivoEscolhido;
    try {
      escolhido = { nome: file.name, tipo, texto: await lerTexto(file) };
    } catch {
      setArquivo(null);
      setPasso({
        tipo: 'recusa',
        codigo: 'LEITURA_DO_ARQUIVO',
        mensagem: 'Não foi possível ler este arquivo no navegador. Escolha o arquivo de novo.',
        recusas: [],
      });
      return;
    }
    setArquivo(escolhido);

    /*
      A primeira conferência sai daqui, sozinha, com o mapeamento VAZIO.

      Ela vai recusar, de propósito: é dessa recusa que saem as colunas do
      arquivo, analisadas pelo mesmo analisador que a prévia usa. Sem isto, a
      tela teria de pedir à pessoa o nome exato de uma coluna que ela ainda não
      viu, ou analisar o CSV por conta própria.
    */
    await conferir({}, escolhido);
  }

  async function conferir(mapa: MapeamentoDeColunasDaImportacao, alvo: ArquivoEscolhido) {
    setPasso(null);
    setResultado(null);
    setOcupado(true);
    try {
      const resposta = await api.audienceImportPreview({
        filename: alvo.nome,
        file_type: alvo.tipo,
        content: alvo.texto,
        column_mapping: mapa,
      });
      setPrevia(resposta);
      setCatalogo({
        headers: resposta.headers,
        mappable_fields: resposta.mappable_fields,
        matching_fields: resposta.matching_fields,
      });
      setMapeamentoDaPrevia(JSON.stringify(mapa));
    } catch (e) {
      setPrevia(null);
      setMapeamentoDaPrevia(null);
      tratarRecusa(e, mapa, setPasso, setCatalogo);
    } finally {
      setOcupado(false);
    }
  }

  async function importar(linhasQueEntram: number) {
    if (arquivo === null) return;
    setPasso(null);
    setOcupado(true);
    try {
      const r = await api.audienceImportCommit({
        filename: arquivo.nome,
        file_type: arquivo.tipo,
        content: arquivo.texto,
        column_mapping: mapeamento,
        /*
          O número que ENTRA, e nunca o total do arquivo.

          É o mesmo número que a tela mostrou e que o botão ecoou. O servidor
          recalcula e recusa sem gravar se divergir, e essa segunda conferência
          só serve se a primeira estiver certa.
        */
        confirmed_rows: linhasQueEntram,
        lawful_basis_note: baseLegal,
      });
      setResultado({ importId: r.import_id, linhasGravadas: r.rows_imported });
      setPrevia(null);
      setMapeamentoDaPrevia(null);
      aoImportar();
    } catch (e) {
      tratarRecusa(e, mapeamento, setPasso, setCatalogo);
    } finally {
      setOcupado(false);
    }
  }

  function trocarMapeamento(campo: string, coluna: string) {
    setMapeamento((atual) => {
      const proximo = { ...atual };
      if (coluna === '') delete proximo[campo];
      else proximo[campo] = coluna;
      return proximo;
    });
    setResultado(null);
  }

  const previaEhDoMapeamentoAtual =
    previa !== null && mapeamentoDaPrevia === JSON.stringify(mapeamento);

  return (
    <section className="mt-6" aria-labelledby="importador">
      <Card>
        <h2
          id="importador"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Arquivo e mapeamento
        </h2>

        <div className="mt-3">
          <label htmlFor="arquivo-da-importacao" className="block text-xs text-ink-muted">
            Arquivo (CSV)
          </label>
          <input
            id="arquivo-da-importacao"
            type="file"
            accept=".csv,.xlsx,text/csv"
            onChange={(e) => void escolherArquivo(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full rounded border border-line px-2 py-1 text-sm"
          />
        </div>

        {arquivo === null ? (
          <p className="mt-3 text-ink-muted">
            Escolha um arquivo para começar. Ele não sai do navegador antes de você escolhê-lo, e o
            primeiro envio serve para o servidor ler as colunas.
          </p>
        ) : (
          <p className="mt-2 text-xs text-ink-faint">
            {arquivo.nome} · {formatNumber(arquivo.texto.length)} caractere(s) lido(s) no navegador
            · tipo {arquivo.tipo}
          </p>
        )}

        {/*
        O passo em que o arquivo JÁ foi lido e o mapeamento ainda não existe.

        Neutro de propósito: quem acabou de escolher um arquivo não errou nada, e
        um bloco vermelho aqui ensinaria a ignorar bloco vermelho.
      */}
        {passo?.tipo === 'aguardando_mapeamento' ? (
          <div
            className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3"
            data-testid="aguardando-mapeamento"
          >
            <p className="text-sm font-medium">
              Arquivo lido. Aponte ao menos um identificador para continuar.
            </p>
            <p className="mt-1 text-sm">
              As colunas abaixo são as que o servidor leu no seu arquivo. Enquanto nenhuma coluna
              identificar a pessoa, não há o que contar: a lista não casaria com ninguém na
              plataforma.
            </p>
            {passo.recusas.length === 0 ? null : (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-ink-muted">
                {passo.recusas.map((r) => (
                  <li key={r.code}>{r.reason}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {catalogo === null ? null : (
          <MapeamentoDeColunas
            catalogo={catalogo}
            mapeamento={mapeamento}
            ocupado={ocupado}
            aoTrocar={trocarMapeamento}
            aoConferir={() => {
              if (arquivo !== null) void conferir(mapeamento, arquivo);
            }}
          />
        )}

        {passo?.tipo === 'recusa' ? (
          <div
            className="mt-3 rounded border border-danger-border bg-danger-surface p-3"
            data-testid="recusas"
          >
            {/* A mensagem do SERVIDOR, e não um texto nosso: é ela que diz o que
              fazer, inclusive na recusa de XLSX e na de arquivo grande demais,
              que carrega o limite real. */}
            <p className="text-sm">{passo.mensagem}</p>
            <p className="mt-1 text-xs text-ink-faint">Código: {passo.codigo}</p>
            {passo.recusas.length === 0 ? null : (
              /* TODAS as recusas, e não a primeira: consertar uma e descobrir a
               seguinte é a forma de gastar uma tarde num arquivo só. */
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {passo.recusas.map((r) => (
                  <li key={r.code}>
                    {r.reason} <span className="text-xs text-ink-faint">({r.code})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {previa !== null && !previaEhDoMapeamentoAtual ? (
          <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3 text-sm">
            O mapeamento mudou depois desta conferência. Confira de novo: o número que está na tela
            foi calculado com o mapeamento anterior, e não descreve o que entraria agora.
          </p>
        ) : null}

        {previa !== null && previaEhDoMapeamentoAtual ? (
          <>
            <ContagensDaPrevia previa={previa} />
            <Amostra previa={previa} />
            <BaseLegal valor={baseLegal} aoTrocar={setBaseLegal} />
            <Confirmacao
              titulo={`Importar "${arquivo?.nome ?? ''}"`}
              oQueVaiAcontecer="As linhas abaixo viram uma lista de audiência, com os identificadores cifrados. O que está repetido, inválido ou sem identificador não entra, e o servidor confere estes números de novo antes de gravar."
              numeros={[
                {
                  rotulo: 'Entram na lista',
                  valor: formatNumber(previa.rows_that_will_be_imported),
                  detalhe: 'é este número que volta ao servidor',
                },
                {
                  rotulo: 'Linhas no arquivo',
                  valor: formatNumber(previa.counts.total),
                  detalhe: 'inclui repetida, inválida e sem identificador',
                },
                {
                  rotulo: 'Repetidas dentro do arquivo',
                  valor: formatNumber(previa.counts.duplicated_in_file),
                  detalhe: 'entram uma vez só',
                },
                {
                  rotulo: 'Sem identificador utilizável',
                  valor: formatNumber(previa.counts.invalid),
                  detalhe: 'nunca casariam com ninguém',
                },
              ]}
              textoDoBotao={`Importar ${formatNumber(previa.rows_that_will_be_imported)} linha(s)`}
              aoConfirmar={() => void importar(previa.rows_that_will_be_imported)}
              aoCancelar={() => {
                setPrevia(null);
                setMapeamentoDaPrevia(null);
              }}
              ocupado={ocupado}
              impedimento={impedimentoDaImportacao(previa.rows_that_will_be_imported, baseLegal)}
            />
          </>
        ) : null}

        {resultado === null ? null : (
          <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
            Importação {resultado.importId} criada com {formatNumber(resultado.linhasGravadas)}{' '}
            linha(s) gravada(s).
          </p>
        )}
      </Card>
    </section>
  );
}

/**
 * Lê o arquivo escolhido como texto, no navegador.
 *
 * `FileReader`, e não `file.text()`: o segundo não existe no ambiente em que os
 * testes rodam (jsdom 25 não implementa `Blob.prototype.text`, medido nesta
 * máquina), e um caminho que só a produção executa é um caminho que nenhum teste
 * exercita. Manter os dois seria pior ainda: o teste provaria o ramo que o
 * usuário não usa. `FileReader` existe nos dois, então o teste exercita o mesmo
 * código que a pessoa executa.
 *
 * UTF-8 explícito: o padrão do `FileReader` depende da máquina, e planilha
 * exportada em português cheia de acento chegaria com o acento trocado, que é
 * cidade e cargo errados no que a pessoa confere.
 */
function lerTexto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(typeof leitor.result === 'string' ? leitor.result : '');
    leitor.onerror = () => reject(leitor.error ?? new Error('falha ao ler o arquivo'));
    leitor.readAsText(file, 'UTF-8');
  });
}

/** O mínimo que o servidor aceita na declaração. Mesmo número do §188. */
const MINIMO_DA_BASE_LEGAL = 10;

/**
 * O que impede de confirmar, em uma frase.
 *
 * Botão desabilitado sem motivo escrito é a forma de a pessoa concluir que a
 * tela está quebrada.
 */
function impedimentoDaImportacao(linhasQueEntram: number, baseLegal: string): string | null {
  if (linhasQueEntram === 0) {
    return 'Nenhuma linha entraria nesta lista. Importar zero linha não é importar: ajuste o mapeamento ou o arquivo.';
  }
  if (baseLegal.trim().length < MINIMO_DA_BASE_LEGAL) {
    return `Declare a base legal desta lista com ao menos ${MINIMO_DA_BASE_LEGAL} caracteres. Sem a declaração, a lista existe e não envia ninguém.`;
  }
  return null;
}

/**
 * O mapeamento completo, montado com o que a resposta trouxe.
 *
 * Os campos são `mappable_fields` e as opções são `headers`: nenhuma das duas
 * listas é escrita aqui, e as duas chegam tanto pela prévia que passa quanto
 * pela recusa por mapeamento. Os campos que casam pessoa aparecem marcados,
 * porque a importação sem nenhum deles é recusada e o motivo não é óbvio para
 * quem opera.
 */
function MapeamentoDeColunas({
  catalogo,
  mapeamento,
  ocupado,
  aoTrocar,
  aoConferir,
}: {
  catalogo: Catalogo;
  mapeamento: MapeamentoDeColunasDaImportacao;
  ocupado: boolean;
  aoTrocar: (campo: string, coluna: string) => void;
  aoConferir: () => void;
}) {
  return (
    <div className="mt-4" data-testid="mapeamento">
      <p className="text-sm font-medium">Mapeamento de colunas</p>
      <p className="mt-1 text-xs text-ink-muted">
        {formatNumber(catalogo.headers.length)} coluna(s) no arquivo e{' '}
        {formatNumber(catalogo.mappable_fields.length)} campo(s) aceitos pelo servidor. Campo
        marcado com identificador é o que casa a pessoa na plataforma: sem ao menos um, a lista não
        serve para o que ela existe.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {catalogo.mappable_fields.map((campo) => {
          const casa = catalogo.matching_fields.includes(campo);
          return (
            <div key={campo}>
              <label htmlFor={`campo-${campo}`} className="block text-xs text-ink-muted">
                {rotuloDoCampo(campo)}
                {casa ? <span className="ml-1 text-xs text-ink-faint">(identificador)</span> : null}
              </label>
              <select
                id={`campo-${campo}`}
                value={mapeamento[campo] ?? ''}
                onChange={(e) => aoTrocar(campo, e.target.value)}
                className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
              >
                <option value="">não mapear</option>
                {catalogo.headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={aoConferir}
        disabled={ocupado}
        className="mt-3 rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised disabled:opacity-50"
      >
        Conferir o mapeamento
      </button>
    </div>
  );
}

/**
 * Os dois números lado a lado, com o que cada um significa.
 *
 * Mostrar só o total faria a pessoa aprovar um número que ninguém vai importar;
 * mostrar só o que entra esconderia que o arquivo tem mais linha do que isso, e
 * ela concluiria que metade sumiu sem motivo.
 */
function ContagensDaPrevia({ previa }: { previa: AudienceImportPreviewResponse }) {
  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="contagens">
      <div className="rounded border border-line-strong bg-surface p-3">
        <p className="text-xs text-ink-muted">Entram na lista</p>
        <p className="text-lg font-semibold">{formatNumber(previa.rows_that_will_be_imported)}</p>
        <p className="mt-1 text-xs text-ink-faint">
          É este número que o botão confirma e que volta ao servidor.
        </p>
      </div>
      <div className="rounded-[11px] border border-line bg-surface-sunken p-3">
        <p className="text-xs text-ink-muted">Linhas no arquivo</p>
        <p className="text-lg font-semibold">{formatNumber(previa.counts.total)}</p>
        <p className="mt-1 text-xs text-ink-faint">
          Inclui {formatNumber(previa.counts.duplicated_in_file)} repetida(s),{' '}
          {formatNumber(previa.counts.invalid)} sem identificador utilizável e{' '}
          {formatNumber(previa.counts.warning)} com aviso. Nada disso entra duas vezes, e o inválido
          não entra nenhuma.
        </p>
      </div>
    </div>
  );
}

/**
 * A amostra, sem identificador nenhum.
 *
 * O que aparece é o que a resposta traz: os campos descritivos com o valor, e os
 * identificadores só como presença. A conferência que interessa é de FORMATO,
 * isto é, se a coluna de cidade tem cidade dentro. Para isso não é preciso ler o
 * e-mail de ninguém, e a API não o envia.
 */
function Amostra({ previa }: { previa: AudienceImportPreviewResponse }) {
  if (previa.sample.length === 0) {
    return (
      <p className="mt-4 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
        A prévia não trouxe nenhuma linha de amostra.
      </p>
    );
  }

  return (
    <section className="mt-4" aria-labelledby="amostra">
      <Card>
        <h3 id="amostra" className="text-sm font-semibold">
          Amostra de {formatNumber(previa.sample.length)} linha(s)
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          Nenhum e-mail e nenhum telefone aparece aqui, de propósito. Confira o mapeamento pelo
          formato do que aparece em cada campo descritivo, e pelos identificadores que cada linha
          tem.
        </p>

        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-line">
                <th className="py-1 pr-3 font-medium">Linha</th>
                <th className="py-1 pr-3 font-medium">Veredito</th>
                <th className="py-1 pr-3 font-medium">Identificadores presentes</th>
                <th className="py-1 pr-3 font-medium">Campos descritivos</th>
                <th className="py-1 font-medium">Observações</th>
              </tr>
            </thead>
            <tbody>
              {previa.sample.map((l) => (
                <tr key={l.line} className="border-b border-line align-top">
                  <td className="py-1 pr-3">{formatNumber(l.line)}</td>
                  <td className="py-1 pr-3">{ROTULO_DO_VEREDITO[l.verdict] ?? l.verdict}</td>
                  <td className="py-1 pr-3">
                    {l.identifiers_present.length === 0
                      ? 'nenhum'
                      : l.identifiers_present.map((c) => `tem ${rotuloDoCampo(c)}`).join(', ')}
                  </td>
                  <td className="py-1 pr-3">
                    {Object.entries(l.descriptive_fields).length === 0
                      ? 'nenhum campo descritivo mapeado'
                      : Object.entries(l.descriptive_fields)
                          .map(([campo, valor]) => `${rotuloDoCampo(campo)}: ${valor}`)
                          .join(' · ')}
                  </td>
                  <td className="py-1">{l.notes.length === 0 ? '' : l.notes.join(' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
}

/**
 * A declaração de base legal.
 *
 * Obrigatória, e com a consequência escrita ao lado: sem ela a lista existe e
 * não envia ninguém. Quem declara fica registrado como responsável, e isso
 * aparece ANTES de a pessoa escrever, e não depois.
 */
function BaseLegal({ valor, aoTrocar }: { valor: string; aoTrocar: (v: string) => void }) {
  return (
    <div className="mt-4 rounded-[11px] border border-line bg-surface-sunken p-3">
      <label htmlFor="base-legal" className="block text-sm font-medium">
        Base legal desta lista
      </label>
      <p className="mt-1 text-xs text-ink-muted">
        Diga de onde vieram estes contatos e com que base eles podem ser usados. Sem esta declaração
        a lista existe e não envia ninguém: as marcas de exclusão ficam desconhecidas, e
        desconhecido exclui. Quem declara fica registrado como responsável por esta lista, com data
        e identidade, na auditoria.
      </p>
      <textarea
        id="base-legal"
        value={valor}
        rows={3}
        autoComplete="off"
        onChange={(e) => aoTrocar(e.target.value)}
        className="mt-2 w-full rounded border border-line px-2 py-1 text-sm"
      />
      <p className="mt-1 text-xs text-ink-faint">
        {formatNumber(valor.trim().length)} de {formatNumber(MINIMO_DA_BASE_LEGAL)} caracteres
        mínimos.
      </p>
    </div>
  );
}

// ===========================================================================
// As importações já feitas
// ===========================================================================

function ImportacoesJaFeitas({ versao }: { versao: number }) {
  const recurso = useResource<AudienceImportsResponse>(() => api.audienceImports(), [versao]);

  return (
    <section className="mt-8" aria-labelledby="ja-feitas">
      <Card>
        <h2
          id="ja-feitas"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Importações já feitas
        </h2>

        {recurso.kind === 'loading' ? <p className="mt-3 text-ink-muted">Carregando…</p> : null}

        {recurso.kind === 'error' ? (
          /*
          Erro NUNCA degrada para lista vazia: "nenhuma importação" e "não
          consegui ler as importações" levam a ações opostas, e a segunda precisa
          dizer que ninguém deveria concluir nada daqui.
        */
          <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
            Não foi possível ler as importações, então nada é afirmado sobre elas aqui.{' '}
            {recurso.message}
          </p>
        ) : null}

        {recurso.kind === 'ready' && recurso.data.imports.length === 0 ? (
          <p className="mt-3 text-ink-muted">Nenhuma importação foi feita ainda.</p>
        ) : null}

        {recurso.kind === 'ready' && recurso.data.imports.length > 0 ? (
          <ul className="mt-3 space-y-2">
            {recurso.data.imports.map((i) => (
              <li key={i.id} className="rounded border border-line bg-surface p-3">
                <p className="text-sm font-medium">{i.filename}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  {ROTULO_DO_ESTADO_DA_IMPORTACAO[i.status] ?? i.status} · {i.file_type} ·{' '}
                  {formatInstant(i.uploaded_at)} · {formatNumber(i.rows_valid)} válida(s) de{' '}
                  {formatNumber(i.rows_total)} linha(s), {formatNumber(i.rows_warning)} com aviso e{' '}
                  {formatNumber(i.rows_invalid)} inválida(s)
                </p>
                {i.lawful_basis_declared ? (
                  <p className="mt-2 text-xs text-ink-faint">
                    Base legal declarada: {i.lawful_basis_note}
                  </p>
                ) : (
                  <p className="mt-2 rounded border border-warn-border bg-warn-surface p-2 text-xs">
                    Sem declaração de base legal: esta lista existe e não envia ninguém, porque as
                    marcas de exclusão ficam desconhecidas e desconhecido exclui.
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    </section>
  );
}

// ===========================================================================
// Recusa
// ===========================================================================

/**
 * Classifica o que voltou: passo de mapeamento, recusa de verdade ou falha de rede.
 *
 * A distinção que decide a cor da tela é `headers` no corpo mais o mapeamento
 * que FOI ENVIADO. Com colunas de volta e nada mapeado ainda, a pessoa não errou
 * nada: ela acabou de escolher o arquivo, e esta é a resposta normal. Com
 * colunas de volta e algo mapeado, a recusa é recusa. Sem colunas, a leitura
 * falhou e não há seletor a montar, porque não existe coluna a oferecer.
 *
 * O corpo é lido do `ApiError`, que já o carrega, e não por um segundo `fetch`:
 * a extração de erro vive em um lugar só, e uma segunda cópia dela é como o bug
 * sobrevive ao próprio conserto.
 */
function tratarRecusa(
  e: unknown,
  mapaEnviado: MapeamentoDeColunasDaImportacao,
  setPasso: (p: Passo) => void,
  setCatalogo: (c: Catalogo | null) => void,
) {
  if (!(e instanceof ApiError)) {
    setCatalogo(null);
    setPasso({
      tipo: 'recusa',
      codigo: 'NETWORK_ERROR',
      mensagem: 'Não foi possível falar com a API do plano de controle.',
      recusas: [],
    });
    return;
  }

  const corpo = (e.body ?? {}) as CorpoDaRecusaDaImportacao;
  const recusas = Array.isArray(corpo.refusals) ? corpo.refusals : [];
  const catalogo = catalogoDoCorpo(corpo);

  if (catalogo === null) {
    // A leitura falhou: sem coluna, nenhum seletor. Um seletor vazio aqui diria
    // que o arquivo não tem colunas, que é outra afirmação.
    setCatalogo(null);
    setPasso({ tipo: 'recusa', codigo: e.code, mensagem: e.message, recusas });
    return;
  }

  setCatalogo(catalogo);
  setPasso(
    Object.keys(mapaEnviado).length === 0
      ? { tipo: 'aguardando_mapeamento', recusas }
      : { tipo: 'recusa', codigo: e.code, mensagem: e.message, recusas },
  );
}

/**
 * O catálogo que veio na recusa, ou `null`.
 *
 * As três listas vêm juntas ou não vêm: montar um seletor com colunas e sem
 * campos, ou com campos e sem colunas, seria desenhar meio controle.
 */
function catalogoDoCorpo(corpo: CorpoDaRecusaDaImportacao): Catalogo | null {
  if (
    !Array.isArray(corpo.headers) ||
    !Array.isArray(corpo.mappable_fields) ||
    !Array.isArray(corpo.matching_fields)
  ) {
    return null;
  }
  return {
    headers: corpo.headers,
    mappable_fields: corpo.mappable_fields,
    matching_fields: corpo.matching_fields,
  };
}
